import moment from 'moment';
import type { RefObject } from 'react';
import { useCallback } from 'react';
import type {
  TimelineGroupBase,
  TimelineItemBase,
} from './react-calendar-timeline';
import type { EventTimeline } from '../core/Timelines';
import type { ITimelineView } from './TimelineView';
import type { ScenarioTimelineGroup, ScenarioTimelineItem } from './types';

export function scrollToOrders(
  orderIds: number[],
  /** Current value of the timelineRef */
  timeline: ITimelineView<ScenarioTimelineItem, ScenarioTimelineGroup>,
  timelineScroller: Element | undefined,
): void {
  const items = timeline.props.items;
  const groups = timeline.props.groups;

  let minStart: number | null = null;
  let maxEnd: number | null = null;
  const orderGroups: Set<TimelineGroupBase['id']> = new Set();

  const useAllItems = orderIds.length === 0;

  /* Find the earliest timeline item start time and last end time */
  for (const item of items) {
    if (
      !useAllItems &&
      (item.isShadow ||
        !('orders' in item) ||
        !item.orders.some((order) => orderIds.includes(order.id)))
    ) {
      continue;
    }

    minStart = minStart ? Math.min(minStart, item.start_time) : item.start_time;
    maxEnd = maxEnd ? Math.max(maxEnd, item.end_time) : item.end_time;

    orderGroups.add(item.group);
  }

  if (!minStart || !maxEnd) {
    if (orderIds.length) {
      console.warn('Could not find timeline item for any order in', orderIds);
    }
    return;
  }

  /*
   * If current timeline zoom level can fit all focused orders we do not
   * want to change it
   */
  const { visibleTimeStart, visibleTimeEnd } = timeline.state;
  const currentVisibleTimeSpan = visibleTimeEnd - visibleTimeStart;
  const newVisibleTimeSpan = maxEnd - minStart;

  if (currentVisibleTimeSpan >= newVisibleTimeSpan) {
    const oldMidPoint = visibleTimeStart + currentVisibleTimeSpan / 2;
    const newMidPoint = minStart + newVisibleTimeSpan / 2;
    timeline.onScrollTime(newMidPoint - oldMidPoint);
  } else {
    timeline.showPeriod(new Date(minStart), new Date(maxEnd));
  }

  if (!timelineScroller || useAllItems) return;

  /* Find topmost group with one of the orders */
  let topGroupIndex = 0;
  let topGroupScrollTop = null;

  for (const groupId of orderGroups) {
    const groupIndex = groups.findIndex((g) => g.id === groupId);
    const groupTop = timeline.state.groupTops[groupIndex] ?? null;

    if (
      groupTop !== null &&
      (!topGroupScrollTop || groupTop < topGroupScrollTop)
    ) {
      topGroupIndex = groupIndex;
      topGroupScrollTop = groupTop;
    }
  }

  scrollToGroupCenter(timelineScroller, topGroupIndex);
}

/**
 * Tries to scroll timeline so that target group is at center, taking
 * the sticky header size into account
 */
function scrollToGroupCenter(scroller: Element, groupIndex: number) {
  const scrollerHeight = scroller.getBoundingClientRect().height;
  const headerHeight =
    scroller.querySelector('.rct-calendar-header')?.getBoundingClientRect()
      .height ?? 100;

  const groupEl =
    scroller
      .querySelector('.rct-horizontal-lines')
      ?.children.item(groupIndex) ?? null;
  const groupTop =
    groupEl instanceof HTMLElement ? (groupEl?.offsetTop ?? 0) : 0;
  /*
   * Group height can vary depending on amount of overlapping items and
   * timeline compact setting
   */
  const groupHeight = groupEl?.getBoundingClientRect().height ?? 54;

  /* Calculate the ideal scrollTop value to center the target vertically */
  const idealScrollTop = groupTop - scrollerHeight / 2 + groupHeight / 2;
  const spaceCoveredByHeader = Math.min(headerHeight, idealScrollTop);

  /* Adjust the final scrollTop to account for the sticky header */
  const finalScrollTop = Math.max(idealScrollTop - spaceCoveredByHeader, 0);

  scroller.scrollTop = finalScrollTop;
}

export function scrollToToday<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>(timelineRef: RefObject<ITimelineView<CustomItem, CustomGroup>>): void {
  if (timelineRef.current) {
    const diff =
      timelineRef.current.state.visibleTimeEnd -
      timelineRef.current.state.visibleTimeStart;
    const currentTime = new Date().getTime();
    const start = moment(currentTime - (diff * 2) / 5).toDate();
    const end = moment(currentTime + (diff * 3) / 5).toDate();
    timelineRef.current.showPeriodLegacy(start, end);
  }
}

export function useTimelineHandlers<
  CustomItem extends TimelineItemBase,
  CustomGroup extends TimelineGroupBase,
>(
  timelineRef: RefObject<ITimelineView<CustomItem, CustomGroup>>,
  groups: { timeline: EventTimeline | null }[],
  timelineScroller: Element | undefined,
) {
  const getTimelineOffsetTop = useCallback(
    (timeline: EventTimeline): number => {
      if (!timelineRef.current || !timelineScroller) {
        return 0;
      }
      const groupIndex = groups.findIndex(
        (g) => g.timeline?.id === timeline.id,
      );
      if (groupIndex < 0) {
        return 0;
      }
      const groupTop = timelineRef.current.state.groupTops[groupIndex];
      return groupTop - timelineScroller.scrollTop;
    },
    [groups, timelineRef, timelineScroller],
  );

  const scrollToTimeline = useCallback(
    (timeline: EventTimeline, offsetTop?: number) => {
      if (timelineRef.current && timelineScroller) {
        const groupIndex = timelineRef.current.props.groups.findIndex(
          (g) => g.id === timeline.id,
        );
        const groupTop =
          timelineRef.current.state.groupTops[groupIndex] ?? null;
        if (groupTop !== null) {
          timelineScroller.scrollTop = groupTop - (offsetTop ?? 0);
        }
      }
    },
    [timelineRef, timelineScroller],
  );

  return { getTimelineOffsetTop, scrollToTimeline };
}
